import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneIncoming } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface IncomingCallBannerProps {
  call: {
    name: string;
    initials: string;
    colorLight: string;
    colorDark: string;
    group: string;
  };
  onTap: () => void;
  onDismiss: () => void;
}
export function IncomingCallBanner({
  call,
  onTap,
  onDismiss
}: IncomingCallBannerProps) {
  const {
    isLight
  } = useTheme();
  const [visible, setVisible] = useState(false);
  // Deliberate delay before showing — feels like a real incoming call notification
  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(showTimer);
  }, []);
  // Auto-dismiss after visible for 5 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => onDismiss(), 5000);
    return () => clearTimeout(timer);
  }, [visible, onDismiss]);
  if (!visible) return null;
  return <motion.div initial={{
    opacity: 0,
    y: -80,
    scale: 0.92,
    filter: 'blur(8px)'
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)'
  }} exit={{
    opacity: 0,
    y: -60,
    scale: 0.95,
    filter: 'blur(4px)'
  }} transition={{
    type: 'spring',
    stiffness: 220,
    damping: 26,
    mass: 1
  }} className="absolute top-4 left-3 right-3 z-[60] cursor-pointer" onClick={onTap} data-id="element-322">
      <motion.div initial={{
      boxShadow: '0 0 0 0 rgba(0, 200, 83, 0)'
    }} animate={{
      boxShadow: ['0 0 0 0 rgba(0, 200, 83, 0.15)', '0 0 0 8px rgba(0, 200, 83, 0)']
    }} transition={{
      duration: 1.8,
      repeat: Infinity,
      ease: 'easeOut'
    }} className={`relative overflow-hidden rounded-2xl p-3.5 flex items-center gap-3.5 shadow-2xl border ${isLight ? 'bg-white border-slate-200 shadow-black/10' : 'bg-[#1a1a1a] border-white/10 shadow-black/60'}`} data-id="element-323">
        {/* Animated green left accent */}
        <motion.div initial={{
        height: 0
      }} animate={{
        height: '100%'
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: 'easeOut'
      }} className="absolute left-0 top-0 w-[3px] bg-vexu rounded-full" data-id="element-324" />

        {/* Avatar with subtle entrance */}
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.15,
        type: 'spring',
        stiffness: 300,
        damping: 20
      }} className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${isLight ? call.colorLight : call.colorDark}`} data-id="element-325">
          {call.initials}
        </motion.div>

        <div className="flex-1 min-w-0" data-id="element-326">
          <motion.div initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.4
        }} className="flex items-center gap-2" data-id="element-327">
            <h4 className={`text-[14px] font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-328">
              {call.name}
            </h4>
            <span className="flex h-2 w-2 relative" data-id="element-329">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-vexu opacity-75" data-id="element-330"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-vexu" data-id="element-331"></span>
            </span>
          </motion.div>
          <motion.p initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.4
        }} className={`text-xs truncate mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-332">
            AI Agent is handling this call...
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.35,
        type: 'spring',
        stiffness: 300,
        damping: 20
      }} className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-333">
          <PhoneIncoming className="w-4 h-4" data-id="element-334" />
        </motion.div>
      </motion.div>
    </motion.div>;
}